// Global JS: shared utilities, deferred media, slider, and media pause logic
function algoliaStringify(value) {
  return value.trim().toLowerCase().replace(" ", "_").replace("/", "_");
}

// Object Id is the variant id
function addToAlgoliaStorage(productId, queryId, index) {
  const products = JSON.parse(localStorage.getItem('algoliaProducts')) || [];

  products.push({
    productId,
    queryId,
    index,
  });

  localStorage.setItem("algoliaProducts", JSON.stringify(products));
}

function handleize(input) {
  return input
    .toLowerCase() // Convert to lowercase
    .trim() // Remove leading/trailing whitespace
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-'); // Remove consecutive hyphens
}

Number.prototype.moneyWithoutCurrency = function () {
  return (this / 100).toFixed(2);
};

const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (
    /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      ua
    )
  ) {
    return 'mobile';
  }
  return 'desktop';
};

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function JDRounding(value) {
  const intValue = Math.round(value);

  if (intValue <= 8) return 10;
  if (intValue >= 99) return 100;

  if (intValue >= 9 && intValue <= 13) return 10;
  if (intValue >= 14 && intValue <= 18) return 15;
  if (intValue >= 19 && intValue <= 23) return 20;
  if (intValue >= 24 && intValue <= 28) return 25;
  if (intValue >= 29 && intValue <= 33) return 30;
  if (intValue >= 34 && intValue <= 38) return 35;
  if (intValue >= 39 && intValue <= 43) return 40;
  if (intValue >= 44 && intValue <= 48) return 45;
  if (intValue >= 49 && intValue <= 53) return 50;
  if (intValue >= 54 && intValue <= 58) return 55;
  if (intValue >= 59 && intValue <= 63) return 60;
  if (intValue >= 64 && intValue <= 68) return 65;
  if (intValue >= 69 && intValue <= 73) return 70;
  if (intValue >= 74 && intValue <= 78) return 75;
  if (intValue >= 79 && intValue <= 83) return 80;
  if (intValue >= 84 && intValue <= 88) return 85;
  if (intValue >= 89 && intValue <= 93) return 90;
  if (intValue >= 94 && intValue <= 98) return 95;
  return intValue;
}

/**
 * General query function for the Storefront API. Accepts any query supported by Shopify.
 * https://shopify.dev/api/storefront/reference
 *
 * @param   {String} query  A graphQL query, you can test your query here: https://shopify.dev/graphiql/storefront-graphiql
 * @returns {JSON}          The JSON value returned by Shopify
 */
function graphQL(query) {
  return fetch(`https://${Shopify.shop}/api/2022-01/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/graphql',
      'X-Shopify-Storefront-Access-Token': window.theme.storefront_api_token,
    },
    body: query,
  }).then((res) => res.json());
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          'form',
          `${element.form.getAttribute('id')}-${uniqueKey}`
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }

  static updateSourceFromDestination = (html, container, id) => {
    const source = html.getElementById(`${id}`);
    const destination = container.querySelector(`#${id}`);

    if (source && destination) {
      destination.innerHTML = source.innerHTML;

      if (source.classList.contains('hidden')) {
        destination.classList.toggle('hidden');
      }
    }
  };
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute(
    'aria-expanded',
    summary.parentNode.hasAttribute('open')
  );

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute(
      'aria-expanded',
      !event.currentTarget.closest('details').hasAttribute('open')
    );
  });

  if (summary.closest('menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
      '*'
    );
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === 'minus'
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      if (buttonMinus) {
        buttonMinus.classList.toggle(
          'disabled',
          parseInt(value) <= parseInt(this.input.min)
        );
      }
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      if (buttonPlus) {
        buttonPlus.classList.toggle('disabled', value >= max);
      }
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options['hideElement'] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    'change',
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawer = this.querySelector('.drawer');
    this.mainDetailsToggle = this.querySelector('details');
    this.overlay = this.querySelector('.drawer__overlay');

    this.close = this.querySelector('.drawer__close');
    this.drawerClose = this.querySelector('.drawer--close');

    this.addEventListener('keyup', this.onKeyUp.bind(this));

    if (this.mainDetailsToggle) {
      this.summary = this.mainDetailsToggle.querySelector('summary');
      this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    }

    if (this.close) {
      this.close.addEventListener('click', this.closeMenuDrawer.bind(this));
    }

    if (this.drawerClose) {
      this.drawerClose.addEventListener(
        'click',
        this.closeMenuDrawer.bind(this)
      );
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', this.closeMenuDrawer.bind(this));
    }
  }

  onSummaryClick() {
    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.closeMenuDrawer(true);
    } else {
      this.openMenuDrawer();
    }

    if (window.matchMedia('(max-width: 990px)')) {
      document.documentElement.style.setProperty(
        '--viewport-height',
        `${window.innerHeight}px`
      );
    }
  }

  open(target) {
    this.openMenuDrawer(target);
  }

  close() {
    this.closeMenuDrawer();
  }

  openMenuDrawer(target) {
    setTimeout(() => {
      this.drawer.classList.add('drawer--opened');
      document.body.classList.add('overflow-hidden');
    });

    if (this.mainDetailsToggle) {
      this.summary.setAttribute('aria-expanded', true);
      trapFocus(this.mainDetailsToggle, this.summary);
    } else {
      trapFocus(this.drawer);

      if (target) {
        this.triggeringElement = target;
        this.triggeringElement.setAttribute('aria-expanded', true);
      }
    }
  }

  closeMenuDrawer(e) {
    if (e) {
      e.preventDefault();
    }

    setTimeout(() => {
      this.drawer.classList.remove('drawer--opened');
      document.body.classList.remove('overflow-hidden');
    });

    if (this.mainDetailsToggle) {
      setTimeout(() => {
        this.mainDetailsToggle.removeAttribute('open');
      }, 200);

      this.summary.setAttribute('aria-expanded', false);
      removeTrapFocus(this.summary);
    } else {
      if (this.triggeringElement) {
        removeTrapFocus(this.triggeringElement);
        this.triggeringElement.setAttribute('aria-expanded', false);
      } else {
        removeTrapFocus();
      }
    }

    document.body.classList.remove('overflow-hidden');
  }

  onKeyUp(event) {
    if (!event || !event.code || event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    this.closeMenuDrawer();
  }
}

customElements.define('menu-drawer', MenuDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();

    this.removeOnClose = this.dataset.removeOnClose === 'true';

    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this, false)
    );
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (
          event.pointerType === 'mouse' &&
          !event.target.closest('deferred-media, product-model')
        )
          this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();

    const media = this.querySelector('deferred-media');

    if (media) {
      media.loadContent();
    }
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();

    if (this.removeOnClose) {
      this.remove();
    }
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
  
    this.posterButton = this.querySelector('.media__poster');
    this.posterImage = this.querySelector('.media__poster-image');
  
    const autoplay = this.dataset.autoplay === 'true';
  
    if (this.posterButton) {
      this.posterButton
        .addEventListener('click', (e) => {
          e.preventDefault();

          this.loadContent(true, true);
        })
        .bind(this);
    }
  
    if (this.posterImage) {
      this.posterImage
        .addEventListener('click', (e) => {
          e.preventDefault();

          this.loadContent(true, true);
        })
        .bind(this);
    }
  
    // Track current breakpoint bucket so device switch can swap media.
    this.viewportBucket = window.innerWidth < 750 ? 'mobile' : 'desktop';
  
    this._onEditorResize = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        this.handleEditorResize();
      }, 120);
    };

    window.addEventListener('resize', this._onEditorResize);
  
    if (autoplay) {
      this.loadContent(false);
    }
  }

  disconnectedCallback() {
    if (this._onEditorResize) {
      window.removeEventListener('resize', this._onEditorResize);
    }
    if (this._resizeTimer) {
      clearTimeout(this._resizeTimer);
    }
  }
  
  handleEditorResize() {
    const nextBucket = window.innerWidth < 750 ? 'mobile' : 'desktop';
    if (nextBucket === this.viewportBucket) return;
  
    this.viewportBucket = nextBucket;
  
    // Only rehydrate when media is already loaded.
    if (!this.hasAttribute('loaded')) return;
  
    if (typeof window.pauseAllMedia === 'function') {
      window.pauseAllMedia();
    }
  
    // Remove already-injected media nodes and let loadContent pick the right one.
    this.querySelectorAll(':scope > video, :scope > model-viewer, :scope > iframe')
      .forEach((node) => node.remove());
  
    this.removeAttribute('loaded');
    this.loadContent(false);
  }

  // `play` is set by poster clicks so click-to-play videos (no autoplay attribute) start on that click.
  loadContent(focus = true, play = false) {
    //window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(
        this.querySelector('template').content.firstElementChild.cloneNode(true)
      );

      const children = content.querySelectorAll('video, model-viewer, iframe');

      this.setAttribute('loaded', true);

      children.forEach((child, index) => {
        if (
          child.classList.contains('hidden--mobile') &&
          window.innerWidth < 750
        ) {
          return;
        }

        if (
          child.classList.contains('hidden--desktop') &&
          window.innerWidth >= 750
        ) {
          return;
        }

        const deferredElement = this.appendChild(child);

        if (focus) deferredElement.focus();

        if (
          deferredElement.nodeName == 'VIDEO' &&
          (play || deferredElement.getAttribute('autoplay'))
        ) {
          // force autoplay for safari
          const playPromise = deferredElement.play();
          if (playPromise) playPromise.catch(() => {});
        }
      });

      if (this.posterButton) {
        this.posterButton.remove();
      }

      if (this.posterImage) {
        this.posterImage.remove();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const waitForInit = this.dataset.waitForInit == 'true';

    if (!waitForInit) {
      this.init();
    }
  }

  init() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.loop = this.dataset.loop == 'true';
    this.autoplay = this.dataset.autoplay == 'true';
    this.speed = this.dataset.speed || 5000;
    this.forcePaused = this.dataset.forcePaused == 'true';

    this.slider = this.querySelector(':scope > [id^="Slider-"]');
    this.buttons = this.querySelector(':scope > .slider-buttons');

    if (!this.slider) return;

    if (this.buttons) {
      this.prevButton = this.buttons.querySelector('button[name="prev"]');
      this.nextButton = this.buttons.querySelector('button[name="next"]');
      this.dots = this.buttons.querySelector('.slider-counter--dots');

      if (this.prevButton) {
        this.prevButton.addEventListener(
          'click',
          this.onButtonClick.bind(this)
        );
      }

      if (this.nextButton) {
        this.nextButton.addEventListener(
          'click',
          this.onButtonClick.bind(this)
        );
      }
    }

    if (this.autoplay) {
      this.setAutoPlay();
    }

    const debouncedOnScroll = debounce((event) => {
      this.currentSlide = this.getCurrentSlideFromPosition();
      this.currentPage = this.getCurrentPage();

      this.setButtonsState();
      this.setDotsState();
    }, 50);

    this.slider.addEventListener('scroll', debouncedOnScroll.bind(this));

    const resizeObserver = new ResizeObserver((entries) => this.setupPages());
    resizeObserver.observe(this.slider);

    this.setupPages();
  }

  getCurrentSlideFromPosition() {
    return Math.floor(this.slider.scrollLeft / this.sliderItemWidth);
  }

  getCurrentPage() {
    return (
      Math.ceil(Math.abs(this.currentSlide) / this.slidesPerPage) %
      Math.ceil(this.totalPages)
    );
  }

  setupPages() {
    /*
     * Only check for visible slides since some may be hidden based on screen sizes.
     */
    this.sliderItems = this.slider.querySelectorAll(':scope > [id^="Slide-"]');

    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );

    /*
     * If there are less than 2 visible slides, return.
     */
    if (this.sliderItemsToShow.length < 2) return;

    this.sliderItemsToShow.forEach((slide, index) => {
      slide.setAttribute('data-slide-index', index);
    });

    /**
     * Assume that each slide is the same width. Get the width of the first visible slide.
     *
     * Use this width to calculate the number of slides per page.
     */
    this.sliderItemWidth = this.sliderItemsToShow[0].offsetWidth;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
        this.sliderItemWidth
    );

    /**
     * Calculate the total number of pages.
     *
     * If there are is only 1 page, hide the buttons.
     */
    this.totalSlides = this.sliderItemsToShow.length;
    this.totalPages = this.totalSlides / this.slidesPerPage;

    // TODO: Eventually allow for custom slide counts per page.
    this.step = this.slidesPerPage; //Math.ceil(this.slidesPerPage / this.totalPages);

    this.currentSlide = this.getCurrentSlideFromPosition();
    this.currentPage = Math.ceil(this.currentSlide / this.slidesPerPage);

    if (this.buttons) {
      if (this.totalPages <= 1) {
        this.buttons.classList.add('hidden');
      } else {
        this.buttons.classList.remove('hidden');
      }
    }

    if (this.loop) {
      this.nextSlideItemsToShow = this.sliderItemsToShow.slice();

      this.prevSlideItemsToShow = this.sliderItemsToShow.slice();
      this.prevSlideItemsToShow.reverse();

      this.maxSlides = this.totalSlides;
      this.minSlides = 0;
    }

    /**
     * Generate the dots if they exist.
     */
    this.generateDots();
    this.setButtonsState();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll(':scope > [id^="Slide-"]');
    this.setupPages();
  }

  generateDots() {
    if (this.dots) {
      this.dots.innerHTML = '';

      for (let i = 0; i < this.totalPages; i++) {
        const dot = document.createElement('button');

        dot.classList.add('slider-counter__link');
        dot.classList.add('slider-counter__link--dots');
        dot.classList.add('link');

        if (i == 0) {
          dot.classList.add('slider-counter__link--active');
        }

        dot.setAttribute('data-slide-to', i);
        dot.setAttribute('aria-label', `Slide ${i + 1}`);
        dot.setAttribute('aria-controls', this.slider.id);

        dot.addEventListener('click', (event) => {
          const slideTo = event.currentTarget.getAttribute('data-slide-to');
          this.setPage(slideTo);
        });

        const span = document.createElement('span');
        span.classList.add('dot');

        dot.appendChild(span);

        this.dots.appendChild(dot);
      }
    }
  }

  update() {
    if (
      !this.slider ||
      !this.nextButton ||
      this.currentSlide == this.previousSlide
    ) {
      return;
    }

    this.currentPage =
      Math.ceil(Math.abs(this.currentSlide) / this.slidesPerPage) %
      Math.ceil(this.totalPages);

    if (this.loop) {
      /**
       * Handles infinite loop when navigating past the first or last slide.
       * Creates cloned slides at the beginning to maintain seamless scrolling.
       *
       * Steps:
       * 1. Extract slides to clone from their respective arrays
       * 2. Clone the DOM elements for these slides
       * 3. Mark clones with 'slider__slide--clone' class
       * 4. Set proper data-slide-index for each clone
       * 5. Insert clones at the beginning or end of the slider
       */

      if (this.currentSlide < this.minSlides) {
        // 1.
        const slidesToAdd = this.prevSlideItemsToShow.slice(0, this.step);
        this.prevSlideItemsToShow = this.prevSlideItemsToShow
          .slice(this.step)
          .concat(slidesToAdd);

        // 2.
        const clonedSlides = slidesToAdd.map((slide) => slide.cloneNode(true));

        // 3.
        clonedSlides.forEach((slide) =>
          slide.classList.add('slider__slide--clone')
        );

        // 4.
        clonedSlides.forEach((slide, index) => {
          slide.setAttribute(
            'data-slide-index',
            (this.previousSlide || 0) - index - 1
          );
        });

        // 5.
        this.slider.prepend(...clonedSlides.reverse());

        this.minSlides = this.currentSlide;
      } else if (this.currentSlide + this.slidesPerPage > this.maxSlides) {
        // 1.
        const slidesToAdd = this.nextSlideItemsToShow.slice(0, this.step);
        this.nextSlideItemsToShow = this.nextSlideItemsToShow
          .slice(this.step)
          .concat(slidesToAdd);

        // 2.
        const clonedSlides = slidesToAdd.map((slide) => slide.cloneNode(true));

        // 3.
        clonedSlides.forEach((slide) =>
          slide.classList.add('slider__slide--clone')
        );

        // 4.
        clonedSlides.forEach((slide, index) => {
          slide.setAttribute('data-slide-index', this.maxSlides + index);
        });

        // 5.
        this.slider.append(...clonedSlides);

        this.maxSlides = this.maxSlides + this.step;
      }
    } else {
      this.setButtonsState();
    }

    if (this.dots) {
      this.setDotsState();
    }


    const scrollPosition = this.currentSlide * this.sliderItemWidth;
    this.slider.scrollLeft = scrollPosition;
    this.dispatchEvent(
      new CustomEvent('slideChanged', {
        detail: {
          currentPage: this.currentPage,
          currentElement: this.sliderItemsToShow[this.currentSlide],
        },
      })
    );

    this.previousSlide = this.currentSlide;
  }

  setDotsState() {
    if (this.dots) {
      this.dots
        .querySelectorAll('.slider-counter__link')
        .forEach((dot, index) => {
          dot.classList.remove('slider-counter__link--active');
          if (index == this.currentPage) {
            dot.classList.add('slider-counter__link--active');
          }
        });
    }
  }

  setButtonsState() {
    if (!this.loop) {
      if (this.prevButton) {
        if (this.currentPage < 1) {
          this.prevButton.setAttribute('disabled', 'disabled');
        } else {
          this.prevButton.removeAttribute('disabled');
        }
      }

      if (this.nextButton) {
        if (this.currentPage >= this.totalPages - 1) {
          this.nextButton.setAttribute('disabled', 'disabled');
        } else {
          this.nextButton.removeAttribute('disabled');
        }
      }
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();

    const direction = event.currentTarget.name;

    if (this.loop) {
      if (direction == 'next') {
        this.currentSlide += this.step;
      } else if (direction == 'prev') {
        this.currentSlide -= this.step;
      }
    } else {
      if (direction == 'next') {
        this.currentSlide += this.step;

        if (this.currentSlide >= this.totalSlides) {
          this.currentSlide = this.totalSlides - 1;
        }
      } else if (direction == 'prev') {
        this.currentSlide -= this.step;

        if (this.currentSlide < 0) {
          this.currentSlide = 0;
        }
      }
    }

    this.update();
  }

  setSlidePosition(slidePosition) {
    this.currentSlide = slidePosition;
    this.update();
  }

  setPage(page) {
    this.currentSlide = page * this.slidesPerPage;
    this.update();
  }

  setAutoPlay() {
    this.slider.setAttribute('aria-live', 'polite');
    this.slider.setAttribute('aria-atomic', 'true');

    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    this.sliderAutoplayButton = this.querySelector('.slider-button__toggle');

    if (this.sliderAutoplayButton) {
      this.sliderAutoplayButton.addEventListener(
        'click',
        this.autoPlayToggle.bind(this)
      );

      this.play();
    } else {
      this.reducedMotion.matches ? this.pause() : this.play();
    }
  }

  autoPlayToggle() {
    const videos = this.querySelectorAll('video');

    if (this.forcePaused) {
      this.play();
      this.forcePaused = false;

      this.sliderAutoplayButton.setAttribute('data-pause', 'false');
      this.sliderAutoplayButton.setAttribute(
        'aria-label',
        window.accessibilityStrings.pauseSlideshow
      );

      videos.forEach((video) => video.play());
    } else {
      this.pause();
      this.forcePaused = true;

      this.sliderAutoplayButton.setAttribute('data-pause', 'true');
      this.sliderAutoplayButton.setAttribute(
        'aria-label',
        window.accessibilityStrings.playSlideshow
      );

      videos.forEach((video) => video.pause());
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');

    clearInterval(this.autoplayInterval);

    this.autoplayInterval = setInterval(
      this.autoRotateSlides.bind(this),
      this.speed
    );
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplayInterval);
  }

  autoRotateSlides() {
    this.currentSlide += this.step;

    if (this.currentSlide >= this.totalSlides) {
      this.currentSlide = 0;
    }

    this.update();
  }

  focusOutHandling(event) {
    if (!this.forcePaused) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (!this.forcePaused) {
      this.pause();
    }
  }
}

customElements.define('slider-component', SliderComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();

    this.colorSwatches = this.querySelectorAll('.product-form__color');

    this.sizeContainer = this.querySelector(
      '.product-form__input--size,.product-form__input--taille'
    );
    this.sizeSwatches = this.querySelectorAll(
      '.product-form__input--size .product-form__pill,.product-form__input--taille'
    );

    if (this.colorSwatches) {
      this.colorSwatches.forEach((swatch) => {
        swatch.addEventListener('mouseover', () => {
          const parent = swatch.closest('.product-form__input');
          const label = parent.querySelector('.product-form__label');

          const value = swatch.getAttribute('data-value');

          this.selectedValue = label.innerHTML;
          label.innerHTML = value;
        });

        swatch.addEventListener('mouseout', () => {
          const parent = swatch.closest('.product-form__input');
          const label = parent.querySelector('.product-form__label');

          label.innerHTML = this.selectedValue;
        });
      });
    }

    if (this.sizeSwatches) {
      this.sortSizeSwatches();
    }

    if (this.sizeContainer) {
      this.sizeContainer.classList.add('size-swatches-sorted');
    }
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          '--swatch--background',
          swatchValue
        );
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          '--swatch--background',
          'unset'
        );
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked')
    ).map(({ dataset }) => dataset.optionValueId);
  }

  sortSizeSwatches() {
    if (
      !window.theme?.size_order_list ||
      !Array.isArray(window.theme.size_order_list)
    ) {
      return;
    }

    // Convert NodeList to Array for easier manipulation
    const swatchArray = Array.from(this.sizeSwatches);

    // Get the parent container
    const container = swatchArray[0]?.parentElement;
    if (!container) return;

    // Sort the swatches based on the size_order_list
    const sortedSwatches = swatchArray.sort((a, b) => {
      const sizeA = a.getAttribute('data-value');
      const sizeB = b.getAttribute('data-value');

      const indexA = window.theme.size_order_list.indexOf(sizeA);
      const indexB = window.theme.size_order_list.indexOf(sizeB);

      // If size is not found in order list, put it at the end
      const orderA =
        indexA === -1 ? window.theme.size_order_list.length : indexA;
      const orderB =
        indexB === -1 ? window.theme.size_order_list.length : indexB;

      return orderA - orderB;
    });

    // Update the DOM by appending sorted elements
    sortedSwatches.forEach((swatch) => {
      container.appendChild(swatch);
    });

    // Update the sizeSwatches NodeList reference
    this.sizeSwatches = this.querySelectorAll(
      '.product-form__input--size .product-form__pill'
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations();
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations() {
    fetch(`${this.dataset.url}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }

        const googleAnalyticsProducts = [];
        recommendations
          .querySelectorAll('.grid__item')
          .forEach((item, index) => {
            googleAnalyticsProducts.push({
              item_id: item.dataset.id,
              item_name: item.dataset.title,
              affiliation: Shopify.shop,
              index: index,
              item_brand: item.dataset.vendor,
              item_category: item.dataset.type,
              item_list_id: 'Related Products',
              item_list_name: 'Related Products',
              price: item.dataset.price,
              quantity: 1,
            });
          });

        const dataLayerObj = {
          event: 'view_item_list',
          page_location: window.location.href,
          page_referrer: document.referrer,
          page_title: document.title,
          ecommerce: {
            item_list_id: 'Related Products',
            item_list_name: 'Related Products',
            items: googleAnalyticsProducts,
          },
        };

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(dataLayerObj);
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener(
      'storefront:signincompleted',
      this.handleStorefrontSignInCompleted.bind(this)
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class HoverComponent extends HTMLElement {
  constructor() {
    super();

    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');

    this.addEventListener('mouseover', this.handleMouseOver.bind(this));
    this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));

    this.summary.addEventListener('click', this.handleSummaryClick.bind(this));
  }

  handleSummaryClick(e) {
    if (e) {
      e.preventDefault();
    }
    return false;
  }

  handleMouseOver() {
    this.details.setAttribute('open', '');
  }

  handleMouseLeave() {
    this.details.removeAttribute('open');
  }
}

customElements.define('hover-component', HoverComponent);

class ColorGroup extends HTMLElement {
  constructor() {
    super();

    if (
      Object.keys(this.dataset).length > 0 &&
      this.dataset.waitUntilLoaded !== 'true'
    ) {
      this.setup();
    }
  }

  setup() {
    this.groupId = this.dataset.groupId;

    if (this.groupId === undefined) {
      this.remove();
      return;
    }

    this.random = Math.floor(Math.random() * 1000000);
    this.cacheId = `${this.groupId}-${window.cacheModifier}`;

    this.hardFetch = this.dataset.hardFetch === 'true';

    this.reorderSwatches = this.dataset.reorderSwatches === 'true';
    this.limitSwatches = this.dataset.limitSwatches === 'true';
    this.small = this.dataset.small || window.innerWidth < 750 ? true : false;
    
    this.swatchCount = 0;    

    this.currentUrl = this.dataset.productUrl;
    this.currentProduct = this.dataset.productHandle;    

    this.slider = this.querySelector('.slider');
    this.container = this.querySelector('[color-swatches-container]');
    this.limitedEditionContainer = this.querySelector(
      '[color-limited-edition-container]'
    );

    if (this.hardFetch) {
      this.fetchGroup();      
    }
    else {
      this.init();
    }

    this.addEventListener('change', (event) => {
      const target = event.target;

      if (window.theme.dynamic_yield_enabled && DynamicYield) {
        DynamicYield.changeAttribute('Color', target.value);
      }

      publish(PUB_SUB_EVENTS.swatchChange, target);
    });
  }

  init() {
    this.styleUrl = `${window.routes.group_collection}${this.groupId}`;
    this.displayNumber =
      Math.floor(this.clientWidth / (this.small ? 30 : 50)) - 1;

    if (!this.limitSwatches) {
      this.displayNumber = 999;
    }

    if (false && sessionStorage.getItem(this.cacheId) && this.limitSwatches) {
      const swatches = JSON.parse(sessionStorage.getItem(this.cacheId));

      if (this.slider) {
        this.slider.innerHTML = '';
      } else if (this.container) {
        this.container.innerHTML = '';

        if (this.limitedEditionContainer) {
          this.limitedEditionContainer.innerHTML = '';
        }
      } else {
        this.innerHTML = '';
      }

      for (const property in swatches) {
        var swatch = new DOMParser()
          .parseFromString(swatches[property], 'text/html')
          .querySelector('.swatch');

        this.setSwatches(swatch, swatch.dataset.id);
      }

      this.buildMoreSwatch(swatches);

      if (this.bindSwatches) {
        this.bindSwatches();
      }

      if (this.limitedEditionContainer) {
        if (this.limitedEditionContainer.children.length > 0) {
          this.limitedEditionContainer.parentElement.classList.remove('hidden');
        }
      }

      publish(PUB_SUB_EVENTS.swatchesLoaded, {
        data: this,
      });

      // Jank reset all inputs. A "real" fix would require a lot of work.
      this.querySelectorAll('.product__swatch input').forEach((node) => {
        node.checked = false;
      });
      for (var i = 0; i < 10; i++) {
        setTimeout(() => {
          this.querySelectorAll('.product__swatch input').forEach((node) => {
            node.checked = false;
          });
        }, i);
      }
    } else {
      fetch(`${this.styleUrl}?section_id=product_color-swatches`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            'text/html'
          );
          const swatches = html.querySelectorAll('.swatch');

          var swatchList = {};

          if (this.slider) {
            this.slider.innerHTML = '';
          } else if (this.container) {
            this.container.innerHTML = '';

            if (this.limitedEditionContainer) {
              this.limitedEditionContainer.innerHTML = '';
            }
          } else {
            this.innerHTML = '';
          }

          if (
            this.nodeName != 'CARD-GROUP' ||
            (this.nodeName == 'CARD-GROUP' && swatches.length > 1)
          ) {
            swatches.forEach((swatch, index) => {
              this.setSwatches(swatch, index);

              if (
                swatch.dataset.productHandle === this.currentProduct ||
                this.swatchCount <= this.displayNumber
              ) {
                swatchList[swatch.dataset.id] = swatch.outerHTML;
              }
            });
          }

          //sessionStorage.setItem(this.cacheId, JSON.stringify(swatchList));

          this.buildMoreSwatch(swatches);

          if (this.limitedEditionContainer) {
            if (this.limitedEditionContainer.children.length > 0) {
              this.limitedEditionContainer.parentElement.classList.remove(
                'hidden'
              );
            }
          }

          publish(PUB_SUB_EVENTS.swatchesLoaded, {
            data: this,
          });

          if (this.bindSwatches) {
            this.bindSwatches();
          }
        });
    }
  }

  fetchGroup() {
    fetch(`${this.currentUrl}.json`)
      .then((response) => response.json())
      .then((responseJson) => {        
        if(responseJson.product.tags) {
          const tagList = responseJson.product.tags.split(',');

          tagList.forEach((tag) => {
            if(tag.includes('#.Group:')) {
              this.groupId = tag.replace('#.Group:', '');
            }
          });

          if(this.groupId != "") {
            this.dataset.groupId = this.groupId;
            this.init();
          }
          else {
            this.remove();
          }
        }
      });
  }

  setSwatches(swatch, id) {
    var label = swatch.querySelector('label');
    var input = swatch.querySelector('input');

    this.swatchTotal = swatch.dataset.swatchTotal;

    input.setAttribute(
      'name',
      `style-${this.sectionId}-${this.currentProduct}-${this.random}`
    );

    input.setAttribute(
      'id',
      `StyleSwatch-${this.sectionId}-${this.currentProduct}-${this.random}-${id}`
    );

    label.setAttribute(
      'for',
      `StyleSwatch-${this.sectionId}-${this.currentProduct}-${this.random}-${id}`
    );

    if (swatch.dataset.productHandle === this.currentProduct) {
      this.swatchCount++;

      this.addSwatch(swatch, id, true);

      this.defaultMedia = swatch.querySelector(`template[data-media='image']`);

      input.checked = true;
      input.setAttribute('checked', 'checked');
    } else if (this.swatchCount < this.displayNumber) {
      this.swatchCount++;
      this.addSwatch(swatch, id, false);
    }
  }

  addSwatch(swatch, id, isPrepend) {
    if (this.slider) {
      const li = document.createElement('li');
      li.classList.add('slider__slide');
      li.classList.add('grid__item');
      li.id = `Slide-${this.random}-${id}`;

      li.appendChild(swatch);

      if (this.reorderSwatches && isPrepend) {
        this.slider.prepend(li);
      } else {
        this.slider.appendChild(li);
      }
    } else if (this.container) {
      if (swatch.dataset.limitedEdition === 'true') {
        if (this.reorderSwatches && isPrepend) {
          this.limitedEditionContainer.prepend(swatch);
        } else {
          this.limitedEditionContainer.appendChild(swatch);
        }
      } else {
        if (this.reorderSwatches && isPrepend) {
          this.container.prepend(swatch);
        } else {
          this.container.appendChild(swatch);
        }
      }
    } else {
      if (this.reorderSwatches && isPrepend) {
        this.prepend(swatch);
      } else {
        this.appendChild(swatch);
      }
    }
  }

  buildMoreSwatch(swatches) {
    if (this.swatchCount > this.displayNumber) {
      var lastSwatch = this.querySelector('.swatch:last-child');

      if (lastSwatch) {
        lastSwatch.remove();
      }
    }

    if (this.swatchTotal > this.displayNumber) {
      var div = document.createElement('div');
      div.classList.add('swatch');
      div.classList.add('swatch--more');
      const root = window.Shopify?.routes?.root || "";
      div.innerHTML = `
        <quickview-trigger 
          class="link swatch__label swatch__label--more"
          data-product-handle="${this.currentProduct}"
          data-product-url="${window.location.origin}${root}${this.currentUrl}"
        >
          +${this.swatchTotal - this.displayNumber}
        </quickview-trigger>
      `;

      this.appendChild(div);
    }
  }
}

customElements.define('color-group', ColorGroup);

class CardGroup extends ColorGroup {
  constructor() {
    super();

    if (
      Object.keys(this.dataset).length > 0 &&
      this.dataset.waitUntilLoaded !== 'true'
    ) {
      this.setupElements();
    }
  }

  setupElements() {
    if (this.dataset.hardLoad != 'false') {
      super.setup();
    }
  }

  handleSwatchChange(target) {
    if (!this.contains(target)) return;

    this.card = this.closest('.card');
    this.imageContainer = this.card.querySelector('.card__image-container');
    this.imageWrapper = this.card.querySelector('.card__image-wrapper');
    this.cardTitleLink = this.card.querySelector('.card__title-title');
    this.cardLink = this.card.querySelector('.card__link');
    this.cardPrice = this.card.querySelector('.card__price');

    this.percentOffBadge = this.card.querySelector('.badge--percent-off');

    const swatch = target.closest('.swatch');

    // Change image
    const media = swatch.querySelector('template[data-media="image"]');

    this.imageContainer.innerHTML = '';
    this.imageContainer.appendChild(
      media.content.firstElementChild.cloneNode(true)
    );

    // Update title
    this.cardTitleLink.innerHTML = swatch.dataset.productTitle;
    this.cardTitleLink.href = swatch.dataset.productUrl;

    // Update link url
    this.cardLink.href = swatch.dataset.productUrl;

    // Update global access
    if (swatch.dataset.globalAccess === 'true') {
      this.card.classList.add('card--global-access');
    } else {
      this.card.classList.remove('card--global-access');
    }

    // Update price
    if (this.cardPrice) {
      this.cardPrice.innerHTML =
        swatch.querySelector('.swatch__price').innerHTML;
    }

    if (swatch.dataset.price && swatch.dataset.comparePrice) {
      if (this.percentOffBadge) {
        this.percentOffBadge.remove();
      }

      const price = parseInt(swatch.dataset.price);
      const comparePrice = parseInt(swatch.dataset.comparePrice);

      if (price < comparePrice) {
        const percentOff = ((comparePrice - price) / comparePrice) * 100;

        const div = document.createElement('div');
        div.classList.add('badge', 'badge--percent-off', 'card__price-badge');
        div.innerHTML = `<rounding-component data-value="${percentOff}"></rounding-component>
            ${window.cards.percent_off}`;

        this.imageWrapper.append(div);
      }
    }

    // Update quickview trigger
    this.quickviews = this.card.querySelectorAll('quickview-trigger');

    if (this.quickviews) {
      this.quickviews.forEach((quickview) => {
        quickview.dataset.productHandle = swatch.dataset.productHandle;
        quickview.dataset.productUrl = swatch.dataset.productUrl;
      });
    }

    // Update Wishlist button
    if (window.theme.wishlist_enabled) {
      const currentWishlistButton =
        this.card.querySelector('.wishlist__button');

      if (currentWishlistButton) {
        currentWishlistButton.remove();
      }

      const wishlistContainer = document.createElement('div');
      wishlistContainer.classList.add('wishlist__button');

      const wishlistButton = document.createElement('button');
      wishlistButton.classList.add(
        'link',
        'swym__button',
        'product_' + swatch.dataset.id
      );
      wishlistButton.dataset.withEpi = 'true';
      wishlistButton.dataset.swaction = 'addToWishlist';
      wishlistButton.dataset.productId = swatch.dataset.id;
      wishlistButton.dataset.variantId = swatch.dataset.variantId;
      wishlistButton.dataset.productUrl = `${location.origin}/products/${swatch.dataset.productHandle}`;

      wishlistContainer.appendChild(wishlistButton);

      this.imageWrapper.appendChild(wishlistContainer);

      document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
    }
  }

  connectedCallback() {
    this.onSwatchChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.swatchChange,
      this.handleSwatchChange.bind(this)
    );
  }

  disconnectedCallback() {
    this.onSwatchChangeUnsubscriber();
  }
}

customElements.define('card-group', CardGroup);

class QuickviewGroupSwatches extends ColorGroup {
  constructor() {
    super();

    this.quickview = this.closest('.quickview');
  }

  connectedCallback() {
    this.onSwatchChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.swatchesLoaded,
      this.initializeSlider.bind(this)
    );
  }

  disconnectedCallback() {
    this.onSwatchChangeUnsubscriber();
  }

  initializeSlider() {
    const sliderComponent = this.querySelector('slider-component');

    if (sliderComponent) {
      sliderComponent.init();
    }
  }
}

customElements.define('quickview-group', QuickviewGroupSwatches);

class ProductGroupSwatches extends ColorGroup {
  constructor() {
    super();
  }
}

customElements.define('product-group', ProductGroupSwatches);

class QuickviewTrigger extends HTMLElement {
  constructor() {
    super();

    this.productHandle = this.dataset.productHandle;
    this.productUrl = this.dataset.productUrl;

    this.addEventListener('click', this.openQuickview.bind(this));
  }

  openQuickview(e) {
    if (e) {
      e.preventDefault();
    }

    this.productHandle = this.dataset.productHandle;
    this.productUrl = this.dataset.productUrl;

    const quickview = document.querySelector('#Quickview');

    if (quickview) {
      quickview.remove();
    }

    const quickviewUrl = `${this.productUrl}${
      this.productUrl.includes('?') ? '&' : '?'
    }section_id=quickview`;

    fetch(quickviewUrl)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const quickviewModal = html.querySelector('#Quickview');

        if (quickviewModal) {
          document.body.appendChild(quickviewModal);
          quickviewModal.open(e.target);
        }
      });
  }
}

if (!customElements.get('quickview-trigger')) {
  customElements.define('quickview-trigger', QuickviewTrigger);
}

class QuickviewContainer extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.onVariantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.optionValueSelectionChange,
      this.handleOptionValueChange.bind(this)
    );

    this.onSwatchChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.swatchChange,
      this.handleGroupSwatchChange.bind(this)
    );

    this.onVariantUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.variantChange,
      (event) => {
        const data = event.data;
        if (data.sectionId === this.dataset.sectionId && data.variant) {
          if (window.theme.dynamic_yield_enabled && DynamicYield) {
            DynamicYield.updateRecomendationContext(data.variant.sku);
          }
        }
      }
    );

    this.dispatchEvent(new CustomEvent('quickview:loaded', { bubbles: true }));
  }

  disconnectedCallback() {
    this.onVariantChangeUnsubscriber();
  }

  handleGroupSwatchChange(target) {
    if (!this.contains(target)) return;

    const swatch = target.closest('.swatch');

    const url = this.buildRequestUrl(swatch.dataset.productUrl);

    this.renderProductInfo({
      requestUrl: url,
      targetId: target.id,
      callback: this.handleUpdateProductInfo(),
    });
  }

  handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
    if (!this.contains(event.target)) return;

    if (window.theme.dynamic_yield_enabled && DynamicYield) {
      DynamicYield.changeAttribute(target.dataset.optionName, target.value);
    }

    const url = this.buildRequestUrl(
      this.dataset.productUrl,
      selectedOptionValues
    );

    this.renderProductInfo({
      requestUrl: url,
      targetId: target.id,
      callback: this.handleUpdateProductInfo(),
    });
  }

  buildRequestUrl(url, optionValues) {
    const params = [];

    params.push(`section_id=quickview`);

    if (optionValues && optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`);
    }

    return `${url}?${params.join('&')}`;
  }

  renderProductInfo({ requestUrl, targetId, callback }) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    fetch(requestUrl, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        callback(html);
      })
      .then(() => {
        document.querySelector(`#${targetId}`)?.focus();
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  handleUpdateProductInfo() {
    return (html) => {
      const variant = this.getSelectedVariant(html);

      const quickviewNew = html.querySelector('quickview-container');

      this.dataset.sectionId = quickviewNew.dataset.sectionId;
      this.dataset.productUrl = quickviewNew.dataset.productUrl;

      HTMLUpdateUtility.updateSourceFromDestination(
        html,
        this,
        'QuickviewProduct'
      );
      HTMLUpdateUtility.updateSourceFromDestination(
        html,
        this,
        'QuickviewSwatchesText'
      );
      HTMLUpdateUtility.updateSourceFromDestination(
        html,
        this,
        'QuickviewVariants'
      );
      HTMLUpdateUtility.updateSourceFromDestination(
        html,
        this,
        'QuickviewActions'
      );
      HTMLUpdateUtility.updateSourceFromDestination(
        html,
        this,
        'QuickviewInventory'
      );

      publish(PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId: this.dataset.sectionId,
          html,
          variant,
        },
      });
    };
  }

  getSelectedVariant(html) {
    const selectedVariant = html.querySelector(
      'variant-selects [data-selected-variant]'
    )?.innerHTML;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }
}

customElements.define('quickview-container', QuickviewContainer);

class KlaviyoForm extends HTMLElement {
  constructor() {
    super();

    const source = this.dataset.source;
    const form = this.querySelector('form');
    if (!form) {
      return;
    }
    KlaviyoSubscribe.attachToForms(`#${form.id}`, {
      hide_form_on_success: true,
      success: function (form) {
        var email = form[0].querySelector('[type="email"]');

        if (window.theme.dynamic_yield_enabled && DynamicYield) {
          DynamicYield.newsletterSubscription(email.value);
        }

        const string = `
            <div class='signup-form__success'>
              <h3>${window.theme.klaviyo_success.title}</h3>
              <p>${window.theme.klaviyo_success.message}</p>
            </div>
          `;

        form[0].closest('klaviyo-form').parentElement.innerHTML = string;
      },
      error: function (form) {
        console.error('error');
      },
      success_message: window.theme.klaviyo_success.message,
      extra_properties: {
        $source: source,
      },
    });
  }
}

customElements.define('klaviyo-form', KlaviyoForm);

class TabPanel extends HTMLElement {
  constructor() {
    super();
    this.tabButtonsContainer = this.querySelector('.tab-buttons-container');
    this.tabs = this.querySelectorAll('[role="tab"]');
    this.panels = this.querySelectorAll('[role="tabpanel"]');
    this.initTabs();
  }

  initTabs() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.selectTab(tab));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, tab));
    });
  }

  selectTab(selectedTab) {
    this.panels.forEach((panel) => (panel.style.display = 'none'));
    this.tabs.forEach((tab) => tab.setAttribute('aria-selected', 'false'));

    const panel = this.querySelector(
      `#${selectedTab.getAttribute('aria-controls')}`
    );
    panel.style.display = 'block';
    selectedTab.setAttribute('aria-selected', 'true');
    selectedTab.focus();
  }

  onKeydown(event, tab) {
    const key = event.key;
    let newTab;

    if (key === 'ArrowLeft' || key === 'ArrowUp') {
      newTab = tab.previousElementSibling || this.tabs[this.tabs.length - 1];
    } else if (key === 'ArrowRight' || key === 'ArrowDown') {
      newTab = tab.nextElementSibling || this.tabs[0];
    } else if (key === 'Home') {
      newTab = this.tabs[0];
    } else if (key === 'End') {
      newTab = this.tabs[this.tabs.length - 1];
    }

    if (newTab) {
      event.preventDefault();
      this.selectTab(newTab);
    }
  }
}

customElements.define('tab-panel', TabPanel);

class CardComponent extends HTMLElement {
  constructor() {
    super();

    this.isDy = this.dataset.isDy === 'true';

    if (window.innerWidth > 750) {
      this.addEventListener('mouseover', this.handleMouseOver.bind(this));
      this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
    }

    if (this.isDy) {
      this.imageWrapper = this.querySelector('.card__image-wrapper');

      this.price = parseInt(this.dataset.price);
      this.compareAtPrice = this.dataset.compareAtPrice;      

      if (this.price < this.compareAtPrice) {
        const percentOff = ((this.compareAtPrice - this.price) / this.compareAtPrice) * 100;

        const div = document.createElement('div');
        div.classList.add('badge', 'badge--percent-off', 'card__price-badge');
        div.innerHTML = `<rounding-component data-value="${percentOff}"></rounding-component>
            ${window.cards.percent_off}`;

        this.imageWrapper.append(div);
      }
    }
  }

  handleMouseOver() {
    const slider = this.querySelector('.slider');

    if (slider) {
      const slides = slider.querySelectorAll('.slider__slide');
      const slideWidth = slides[0].clientWidth;

      slider.scrollTo({
        left: slideWidth,
        behavior: 'instant',
      });
    }
  }

  handleMouseLeave() {
    const slider = this.querySelector('.slider');

    if (slider) {
      slider.scrollTo({
        left: 0,
        behavior: 'instant',
      });
    }
  }
}

customElements.define('card-component', CardComponent);

class RoundingComponent extends HTMLElement {
  constructor() {
    super();

    this.value = this.dataset.value;

    const roundedValue = JDRounding(parseFloat(this.value));
    this.innerHTML = `${roundedValue}`;
  }
}

customElements.define('rounding-component', RoundingComponent);

class PriceComponent extends HTMLElement {
  constructor() {
    super();

    this.price = parseFloat(this.dataset.price);
    this.compareAtPrice = this.dataset.compareAtPrice;

    if(this.compareAtPrice) {
      this.compareAtPrice = parseFloat(this.compareAtPrice);
    }
    else {
      this.compareAtPrice = 0;
    }

    if (this.compareAtPrice > this.price) {
      this.classList.add('price--on-sale');
    }

    this.innerHTML = `
      <div class="price__sale">
        <span class="visually-hidden visually-hidden--inline">Sale Price</span>
        <span class="price-item">
          $${this.price}
        </span>
      </div>            

      ${
        this.compareAtPrice > this.price
          ? `
        <div class="price__regular body body--small gray">
          <span class="visually-hidden visually-hidden--inline">Regular Price</span>
          <s class="price-item">
            $${this.compareAtPrice}
          </s>
        </div>
      `
          : ''
      }
    `;
  }
}

customElements.define('price-component', PriceComponent);




class InventoryContainer extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('.inventory__form');
    this.finder = this.querySelector('.inventory__finder-button');

    this.form.addEventListener('submit', this.handleSubmit.bind(this));
    this.finder.addEventListener('click', this.handleFinderClick.bind(this));

    this.storeOrder = localStorage.getItem('storeOrder');

    if(this.storeOrder) {
      this.storeOrder = JSON.parse(this.storeOrder);

      this.setStoreOrder();
    }
  }

  handleFinderClick(event) {
    event.preventDefault();

    if (navigator.geolocation) {
      console.log("navigator.geolocation is supported");
      navigator.geolocation.getCurrentPosition(this.handleFinderSuccess.bind(this), this.handleFinderError.bind(this));
    } else {
      console.log("navigator.geolocation is not supported");
      this.setClassState("permission_error");
    }
  }

  handleFinderSuccess(position) {
    this.setClassState("loading");
    const { latitude, longitude } = position.coords;
    this.reorderStores(longitude, latitude);
    this.setStoreOrder();
  }

  handleFinderError(error) {
    this.setClassState("permission_error");
  }

  async handleSubmit(event) {
    event.preventDefault();

    const formData = new FormData(this.form);
    const location = formData.get('location');

    this.setClassState("loading");
    this.classList.remove('inventory--expanded');

    const locationResponse = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${location}&key=AIzaSyDWlCC7oZbUQ1XkXcjzD5EiuzXnx88P_6c`
    );
    const locationData = await locationResponse.json();

    if(locationData.results.length > 0) {
      const longitude = locationData.results[0].geometry.location.lng;
      const latitude = locationData.results[0].geometry.location.lat;

      this.reorderStores(longitude, latitude);
      this.setStoreOrder();
    }
    else {
      this.setClassState("error");
      return;
    }
  }

  reorderStores(userLongitude, userLatitude) {
    const storeItems = Array.from(this.querySelectorAll('.inventory__result-item'));
    const storeOrder = storeItems.map((storeItem) => {
      const distance = this.calculateDistance(
        userLongitude,
        userLatitude,
        parseFloat(storeItem.dataset.storeLongitude),
        parseFloat(storeItem.dataset.storeLatitude)
      );

      return {
        storeNumber: storeItem.dataset.storeNumber,
        distance,
      };
    });

    storeOrder.sort((a, b) => a.distance - b.distance);

    this.storeOrder = storeOrder;
    localStorage.setItem('storeOrder', JSON.stringify(storeOrder));
  }

  setStoreOrder() {
    const storeItems = Array.from(this.querySelectorAll('.inventory__result-item'));
    const storeOrder = this.storeOrder || JSON.parse(localStorage.getItem('storeOrder'));

    if(!storeItems.length || !storeOrder || !storeOrder.length) {
      return;
    }

    const orderLookup = new Map(
      storeOrder.map((store, index) => [store.storeNumber?.toString() ?? '', index])
    );

    storeItems.sort((a, b) => {
      const aIndex = orderLookup.has(a.dataset.storeNumber)
        ? orderLookup.get(a.dataset.storeNumber)
        : Number.MAX_SAFE_INTEGER;
      const bIndex = orderLookup.has(b.dataset.storeNumber)
        ? orderLookup.get(b.dataset.storeNumber)
        : Number.MAX_SAFE_INTEGER;

      return aIndex - bIndex;
    });

    const listContainer = storeItems[0].parentElement;

    if(listContainer.querySelector(".inventory__result-more")) {
      listContainer.querySelector(".inventory__result-more").remove();
    }

    storeItems.forEach((storeItem) => { 
      const distance = storeOrder.find((item) => item.storeNumber === storeItem.dataset.storeNumber)?.distance;
      storeItem.querySelector('.inventory__store-distance').innerHTML = `${typeof distance == "number" ? `${distance} ${window.language.distance_away}` : 'N/A'}`;
      listContainer.appendChild(storeItem);
    });

    const moreButton = document.createElement('li');
    moreButton.classList.add('inventory__result-more');
    moreButton.innerHTML = `
      <button class="inventory__result-more-button button">
        ${window.language.view_more_stores}
      </button>
      <p class="inventory__result-more-warning body">
        ${window.language.view_more_warning}
      </p>
    `;

    listContainer.appendChild(moreButton);

    moreButton.addEventListener('click', () => {
      this.showMoreStores();
    });

    this.setClassState("loaded");
  }

  showMoreStores() {
    this.classList.add('inventory--expanded');
  }

  calculateDistance(userLongitude, userLatitude, longitude, latitude) {	
    const earthRadiusMeters = 6371e3;
    const storeLatitudeRadians = latitude * Math.PI / 180;
    const userLatitudeRadians = userLatitude * Math.PI / 180;
    const latitudeDifferenceRadians = (userLatitude - latitude) * Math.PI / 180;
    const longitudeDifferenceRadians = (userLongitude - longitude) * Math.PI / 180;
    
    const haversine =
      Math.sin(latitudeDifferenceRadians / 2) * Math.sin(latitudeDifferenceRadians / 2) +
      Math.cos(storeLatitudeRadians) * Math.cos(userLatitudeRadians) *
      Math.sin(longitudeDifferenceRadians / 2) * Math.sin(longitudeDifferenceRadians / 2);
    const centralAngle = 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
    
    const distanceInMeters = earthRadiusMeters * centralAngle;
    const distanceInKM = distanceInMeters / 1000;

    return parseInt(distanceInKM);
  }  

  setClassState(state) {
    this.dataset.state = state;
  }
}

customElements.define('inventory-container', InventoryContainer);




if (!customElements.get('inventory-toggle')) {
  customElements.define(
    'inventory-toggle',
    class InventoryToggle extends HTMLElement {

      constructor() {
        super();  

        this.addEventListener('click', this.handleClick.bind(this));
      }

      handleClick(event) {
        event.preventDefault();

        this.button = this.querySelector('button');

        if(!this.button) {
          return;
        }

        this.productHandle = this.button.dataset.productHandle;
        this.variantId = this.button.dataset.variantId;        

        fetch(`/products/${this.productHandle}?variant=${this.variantId}&section_id=product_inventory`)
          .then(response => response.text())
          .then(responseText => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
           
            const oldInventoryDrawer = document.querySelector('#InventoryDrawer');
            if(oldInventoryDrawer) {
              oldInventoryDrawer.remove();
            }            

            const newInventoryDrawer = html.querySelector('#InventoryDrawer');
            document.body.appendChild(newInventoryDrawer);

            newInventoryDrawer.open(this.button);
          });
      }
    }
  );
}